import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

import MaskInput from 'components/MaskedInput';
import SoilTypeField from 'features/Croptimizer/components/Fields/SoilTypeField';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';

interface SoilSectionProps {
  secSmol: string;
  setSecSmol: (value: string) => void;
  initialSecSmol: string;
  errorSecSmol: string | null;
  fineDrySoil: string;
  setFineDrySoil: (value: string) => void;
  handleFineDrySoil: string | null;
  kg: string;
}

export const SoilSection = ({
  secSmol,
  setSecSmol,
  initialSecSmol,
  errorSecSmol,
  fineDrySoil,
  setFineDrySoil,
  handleFineDrySoil,
  kg,
}: SoilSectionProps) => {
  const { t } = useTranslation();
  const { isExpertMode } = useCroptimizer();

  const getFineDryTitle = useMemo(() => t('fineDrySoil') + ' (cm ⓘ)', [t]);

  const fineDrySoilTooltip = useMemo(
    () => (
      <div>
        <div>{t('fineDryTooltip1')}</div>
        <div>
          {t('example') +
            ' : 10\'000 m2 x 0,2 m x 1,5 x 100% de terre fine (0% de cailloux) = 3\'000 t/ha.'}
        </div>
      </div>
    ),
    [t],
  );

  return (
    <div className={'border-b border-primitiveGrey py-8'}>
      <div className="text-black font-bold text-xl md:text-lg md:mt-6 mb-8">
        {t('soil')}
      </div>
      <div className="flex mx-[-0.5rem]">
        <div className="w-1/3 md:w-full px-2">
          <SoilTypeField isGerman/>
        </div>
        {!isExpertMode &&  
          <div className="w-1/3 md:w-1/2 px-2">
            <MaskInput
              titleClassName={'top-[-9px] left-[8px] truncate max-w-[85%]'}
              title={getFineDryTitle}
              className="w-full"
              tooltipId="Surface x tillage depth x soil density x % of fine soil (< 2 mm)Example : 10'000 m2 x 0,24 m x 1,5 x 85% = 3'060 mt/ha"
              hasTooltip={fineDrySoilTooltip}
              value={fineDrySoil}
              onChange={(value) => setFineDrySoil(value)}
            />
            <div className="text-[#FF3B30] text-xs ml-1 mt-1 mt-0 h-2">
              {handleFineDrySoil && <div> {handleFineDrySoil}</div>}
            </div>
          </div>
        }
      </div>
      {isExpertMode &&
        <div className="flex mx-[-0.5rem] mt-8">
          <div className="w-1/3 md:w-1/2 px-2">
            <MaskInput
              titleClassName="top-[-9px] left-[8px]"
              title={`CEC (Cmol+/${kg})`}
              className="w-full"
              isGreyText={secSmol === initialSecSmol}
              value={secSmol}
              onChange={setSecSmol}
              redBorder={!!errorSecSmol}
            />
            <div className="text-[#FF3B30] text-xs ml-1 mt-1 mt-0 h-2">
              {errorSecSmol && <div> {errorSecSmol}</div>}
            </div>
          </div>
          <div className="w-1/3 md:w-1/2 px-2">
            <MaskInput
              titleClassName={'top-[-9px] left-[8px] truncate max-w-[85%]'}
              title={getFineDryTitle}
              className="w-full"
              tooltipId="Surface x tillage depth x soil density x % of fine soil (< 2 mm)Example : 10'000 m2 x 0,24 m x 1,5 x 85% = 3'060 mt/ha"
              hasTooltip={
                <div>
                  <div>{t('fineDryTooltip1')}</div>
                  <div>
                    {t('example') +
                    ' : 10\'000 m2 x 0,24 m x 1,5 x 85% = 3\'060 mt/ha'}
                  </div>
                </div>
              }
              value={fineDrySoil}
              onChange={(value) => setFineDrySoil(value)}
            />
            <div className="text-[#FF3B30] text-xs ml-1 mt-1 mt-0 h-2">
              {handleFineDrySoil && <div> {handleFineDrySoil}</div>}
            </div>
          </div>
        </div>
      }
    </div>
  );
};
